/**
 * Generate LYN serviceability zones and performed_by strings from bulky serviceability data.
 * 
 * This module:
 * 1. Reads the serviceability CSV
 * 2. Groups suburbs by state and carrier coverage signature
 * 3. Assigns sequential LYN zone codes (LYN-01-NSW, LYN-02-NSW, etc.)
 * 4. Generates LYN_EXTRA_performed_by strings in format: "DT (SYDM/SYDM), SKU (NSW-1/NSW-1)"
 * 5. Outputs a CSV with columns: postcode, suburb, state, courier_zone, pickup, dropoff, onforwarding, LYN_EXTRA_performed_by
 */

interface ServiceabilityRow {
  postcode: string;
  suburb: string;
  state: string;
  courier: string;
  pickup: string;
  dropoff: string;
  pickup_zone: string;
  dropoff_zone: string;
}

interface CarrierInfo {
  courier: string;
  pickup: string;
  dropoff: string;
  pickup_zone: string;
  dropoff_zone: string;
}

interface LocationInfo {
  postcode: string;
  suburb: string;
  state: string;
}

interface OutputRow {
  postcode: string;
  suburb: string;
  state: string;
  courier_zone: string;
  pickup: string;
  dropoff: string;
  onforwarding: string;
  LYN_EXTRA_performed_by: string;
}

/**
 * Parse CSV content into an array of objects
 */
function parseCSV(csvContent: string): ServiceabilityRow[] {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV file is empty or has no data rows');
  }

  const headers = lines[0].split(',').map(h => h.trim());
  const rows: ServiceabilityRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const row: any = {};
    
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });

    rows.push(row as ServiceabilityRow);
  }

  return rows;
}

/**
 * Convert an array of objects to CSV string
 */
function toCSV(rows: OutputRow[]): string {
  if (rows.length === 0) {
    return '';
  }

  const headers = Object.keys(rows[0]);
  const csvLines = [headers.join(',')];

  for (const row of rows) {
    const values = headers.map(header => {
      const value = (row as any)[header] || '';
      // Quote values that contain commas
      return value.toString().includes(',') ? `"${value}"` : value;
    });
    csvLines.push(values.join(','));
  }

  return csvLines.join('\n');
}

/**
 * Normalize text for comparison
 */
function normalizeText(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Create a unique key for a location
 */
function createLocationKey(postcode: string, suburb: string, state: string): string {
  return `${postcode}|${normalizeText(suburb)}|${normalizeText(state)}`;
}

/**
 * Create a signature string for a group of carriers servicing a location.
 * Format: "CARRIER:pickup_zone/dropoff_zone:pickup:dropoff"
 */
function createCarrierSignature(carriers: CarrierInfo[]): string {
  const carrierOrder: Record<string, number> = { 'AMLS': 0, 'DT': 1, 'SKU': 2 };

  const sortedCarriers = carriers.slice().sort((a, b) => {
    const orderA = carrierOrder[a.courier] ?? 99;
    const orderB = carrierOrder[b.courier] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.courier.localeCompare(b.courier);
  });

  const signatureParts = sortedCarriers.map(carrier => {
    return `${carrier.courier}:${carrier.pickup_zone}/${carrier.dropoff_zone}:${carrier.pickup}:${carrier.dropoff}`;
  });

  return signatureParts.join('||');
}

/**
 * Convert new SKU zone codes to old SRB zone naming convention.
 */
function convertSkuZoneToOld(zone: string): string {
  // Direct mapping for zones that remained unchanged
  if (['ACT', 'QLD', 'VIC', 'SA', 'SNC', 'BMO'].includes(zone)) {
    return zone;
  }

  // NSW-1: Sydney metro zones
  if (['NCI', 'NC2', 'NEA', 'NNR', 'NSR', 'NW2', 'NWE', 'SWE', 'BMK', 'SME', 'NSW'].includes(zone)) {
    return 'NSW-1';
  }

  // NSW-2: South Coast
  if (zone === 'SSC') {
    return 'NSW-2';
  }

  // NSW-6: Newcastle/Hunter/Central Coast
  if (zone === 'SNO') {
    return 'NSW-6';
  }

  // Default: return as-is if not in mapping
  return zone;
}

/**
 * Create the LYN_EXTRA_performed_by string.
 * Format: "DT (SYDM/SYDM), SKU (NSW-1/NSW-1)"
 */
function createPerformedByString(carriers: CarrierInfo[], useOldSkuZones: boolean = false): string {
  const carrierOrder: Record<string, number> = { 'AMLS': 0, 'DT': 1, 'SKU': 2 };

  const sortedCarriers = carriers.slice().sort((a, b) => {
    const orderA = carrierOrder[a.courier] ?? 99;
    const orderB = carrierOrder[b.courier] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return a.courier.localeCompare(b.courier);
  });

  const parts = sortedCarriers.map(carrier => {
    let pickupZone = carrier.pickup_zone;
    let dropoffZone = carrier.dropoff_zone;

    // Convert SKU zones to old format if requested
    if (useOldSkuZones && carrier.courier === 'SKU') {
      pickupZone = convertSkuZoneToOld(pickupZone);
      dropoffZone = convertSkuZoneToOld(dropoffZone);
    }

    return `${carrier.courier} (${pickupZone}/${dropoffZone})`;
  });

  return parts.join(', ');
}

/**
 * Determine overall pickup and dropoff capabilities for a location.
 * Returns [pickup, dropoff] where values are 'Y' or 'N'
 */
function determineOverallCapabilities(carriers: CarrierInfo[]): [string, string] {
  const canPickup = carriers.some(c => c.pickup.toUpperCase() === 'Y');
  const canDropoff = carriers.some(c => c.dropoff.toUpperCase() === 'Y');

  return [canPickup ? 'Y' : 'N', canDropoff ? 'Y' : 'N'];
}

/**
 * Get the minimum postcode for a group of locations with the same signature.
 */
function getMinPostcodeForSignature(locationKeys: string[], locationInfo: Map<string, LocationInfo>): number {
  const postcodes = locationKeys.map(key => parseInt(locationInfo.get(key)!.postcode));
  return Math.min(...postcodes);
}

/**
 * Get the alphabetically first suburb name for locations at the minimum postcode.
 */
function getMinSuburbForSignature(locationKeys: string[], locationInfo: Map<string, LocationInfo>): string {
  const minPostcode = getMinPostcodeForSignature(locationKeys, locationInfo);
  const suburbsAtMinPostcode = locationKeys
    .filter(key => parseInt(locationInfo.get(key)!.postcode) === minPostcode)
    .map(key => locationInfo.get(key)!.suburb);
  
  return suburbsAtMinPostcode.length > 0 ? suburbsAtMinPostcode.sort()[0] : '';
}

/**
 * Get a sort key for pickup/dropoff capabilities.
 * Lower values = higher priority (sorted first).
 */
function getCapabilitySortKey(pickup: string, dropoff: string): number {
  if (pickup === 'Y' && dropoff === 'Y') return 0;
  if (pickup === 'Y' && dropoff === 'N') return 1;
  if (pickup === 'N' && dropoff === 'Y') return 2;
  return 3; // N/N
}

/**
 * Main conversion function
 */
export function convertToShippitServiceability(
  csvContent: string,
  useOldSkuZones: boolean = false
): string {
  console.log('\nGenerating LYN courier zones from serviceability data...');
  console.log('Note: Zones are determined by carrier signature (including zone names)');
  console.log('      This ensures granular AMLS zones get separate courier zones\n');

  const serviceabilityData = parseCSV(csvContent);
  console.log(`Loaded ${serviceabilityData.length} records`);

  // Group by location (postcode + suburb + state)
  const locationCarriers = new Map<string, CarrierInfo[]>();
  const locationInfo = new Map<string, LocationInfo>();

  for (const row of serviceabilityData) {
    const locationKey = createLocationKey(row.postcode, row.suburb, row.state);

    // Store location info (normalize suburb to lowercase)
    if (!locationInfo.has(locationKey)) {
      locationInfo.set(locationKey, {
        postcode: row.postcode,
        suburb: row.suburb.trim().toLowerCase(),
        state: row.state,
      });
    }

    // Add carrier info
    if (!locationCarriers.has(locationKey)) {
      locationCarriers.set(locationKey, []);
    }
    locationCarriers.get(locationKey)!.push({
      courier: row.courier.toUpperCase(),
      pickup: row.pickup,
      dropoff: row.dropoff,
      pickup_zone: row.pickup_zone,
      dropoff_zone: row.dropoff_zone,
    });
  }

  console.log(`Found ${locationCarriers.size} unique locations`);

  // Create signature groups by state
  console.log('\nGrouping locations by carrier signature...');

  const stateSignatures = new Map<string, Map<string, string[]>>();

  for (const [locationKey, carriers] of locationCarriers.entries()) {
    const state = locationInfo.get(locationKey)!.state;
    const signature = createCarrierSignature(carriers);

    if (!stateSignatures.has(state)) {
      stateSignatures.set(state, new Map());
    }
    if (!stateSignatures.get(state)!.has(signature)) {
      stateSignatures.get(state)!.set(signature, []);
    }
    stateSignatures.get(state)!.get(signature)!.push(locationKey);
  }

  let totalSignatures = 0;
  for (const sigs of stateSignatures.values()) {
    totalSignatures += sigs.size;
  }
  console.log(`Found ${totalSignatures} unique carrier signatures`);

  // Assign LYN zone codes
  const zoneAssignments = new Map<string, [string, string, string, string]>(); // location_key -> [zone_code, pickup, dropoff, performed_by]
  let globalSequence = 1;

  // Process states in order
  const stateOrder = ['NSW', 'QLD', 'VIC', 'SA', 'ACT', 'NT', 'TAS', 'WA'];

  for (const state of stateOrder) {
    if (!stateSignatures.has(state)) continue;

    // Sort signatures by minimum postcode
    const signatureData: Array<{
      signature: string;
      minPostcode: number;
      capabilityKey: number;
      minSuburb: string;
      locationKeys: string[];
    }> = [];

    for (const [signature, locationKeys] of stateSignatures.get(state)!.entries()) {
      const minPostcode = getMinPostcodeForSignature(locationKeys, locationInfo);
      const minSuburb = getMinSuburbForSignature(locationKeys, locationInfo);

      // Get capability from first location
      const firstLocation = locationKeys[0];
      const carriers = locationCarriers.get(firstLocation)!;
      const [overallPickup, overallDropoff] = determineOverallCapabilities(carriers);
      const capabilityKey = getCapabilitySortKey(overallPickup, overallDropoff);

      signatureData.push({
        signature,
        minPostcode,
        capabilityKey,
        minSuburb,
        locationKeys,
      });
    }

    // Sort by: postcode, capability, suburb, location count, signature
    signatureData.sort((a, b) => {
      if (a.minPostcode !== b.minPostcode) return a.minPostcode - b.minPostcode;
      if (a.capabilityKey !== b.capabilityKey) return a.capabilityKey - b.capabilityKey;
      if (a.minSuburb !== b.minSuburb) return a.minSuburb.localeCompare(b.minSuburb);
      if (a.locationKeys.length !== b.locationKeys.length) return b.locationKeys.length - a.locationKeys.length;
      return a.signature.localeCompare(b.signature);
    });

    for (const { locationKeys } of signatureData) {
      // Get carrier info from first location
      const firstLocation = locationKeys[0];
      const carriers = locationCarriers.get(firstLocation)!;

      // Generate zone code
      const zoneCode = `LYN-${globalSequence.toString().padStart(2, '0')}-${state}`;

      // Generate performed_by string
      const performedBy = createPerformedByString(carriers, useOldSkuZones);

      // Determine overall capabilities
      const [overallPickup, overallDropoff] = determineOverallCapabilities(carriers);

      // Assign to all locations with this signature
      for (const locKey of locationKeys) {
        zoneAssignments.set(locKey, [zoneCode, overallPickup, overallDropoff, performedBy]);
      }

      console.log(`  ${zoneCode}: ${locationKeys.length} locations - ${performedBy} (P:${overallPickup} D:${overallDropoff})`);

      globalSequence++;
    }
  }

  console.log('\n' + '='.repeat(60));
  console.log('ZONE ASSIGNMENT SUMMARY');
  console.log('='.repeat(60));
  console.log(`Total LYN zones generated: ${globalSequence - 1}`);
  console.log(`Total locations: ${zoneAssignments.size}`);

  // Create output rows
  const outputRows: OutputRow[] = [];
  for (const [locationKey, [zoneCode, pickup, dropoff, performedBy]] of zoneAssignments.entries()) {
    const info = locationInfo.get(locationKey)!;
    outputRows.push({
      postcode: info.postcode,
      suburb: info.suburb,
      state: info.state,
      courier_zone: zoneCode,
      pickup,
      dropoff,
      onforwarding: 'N',
      LYN_EXTRA_performed_by: performedBy,
    });
  }

  // Sort by postcode (numeric), then suburb
  outputRows.sort((a, b) => {
    const postcodeA = parseInt(a.postcode);
    const postcodeB = parseInt(b.postcode);
    if (postcodeA !== postcodeB) return postcodeA - postcodeB;
    return a.suburb.localeCompare(b.suburb);
  });

  console.log(`\n✅ Successfully generated ${outputRows.length} rows`);
  if (useOldSkuZones) {
    console.log('   (Using old SKU zone format: NSW-1, NSW-2, NSW-6, etc.)');
  } else {
    console.log('   (Using new SKU zone format: NCI, NC2, SNO, etc.)');
  }

  return toCSV(outputRows);
}

